import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './Dropdown.module.scss';
import bottomAngleIcon from '../../../assets/bottomAngleIcon.svg';
import searchIcon from '../../../assets/search-glass.svg';
import { useUpdateEffect } from '../../../utils/customHooks';
import { isEmpty } from '../../../utils/utils';
import { crossBtnGrey, plusPurpleCircle } from '../../../assets/hostedassets';
import { DropdownOption, CustomOptionConfig } from './dropdownTypes';

interface DropdownProps {
    customClass?: boolean;
    customListClass?: string;
    disabled?: boolean;
    enableSearch?: boolean;
    placeholder?: string;
    label?: string;
    collapseDropdown?: boolean;
    nowrap?: boolean;
    selectedOption: null | DropdownOption;
    optionsList: DropdownOption[];
    itemClickHandler: (item: DropdownOption) => void;
    style?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
    dropdownName?: string;
    hasError?: boolean;
    darkPlaceholder?: boolean;
    errorMessage?: string;
    variant?: 'white' | 'gray';
    size?: 'normal' | 'tiny';
    listStyles?: React.CSSProperties;
    hideOtherOptionWhenNoMatch?: boolean;
    showFloatingLabel?: boolean;
    hideClearIcon?: boolean;
    customOption?: CustomOptionConfig;
}

const Dropdown = ({
    customClass,
    placeholder = 'Select',
    label = '',
    optionsList,
    selectedOption = null,
    itemClickHandler,
    disabled = false,
    enableSearch = false,
    collapseDropdown,
    style = {},
    inputStyle = {},
    hasError = false,
    errorMessage,
    darkPlaceholder = false,
    variant = 'gray',
    size = 'normal',
    listStyles,
    hideOtherOptionWhenNoMatch = false,
    showFloatingLabel = true,
    hideClearIcon = false,
    customOption,
}: DropdownProps) => {
    const [showList, setShowList] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selection, setSelection] = useState<DropdownOption | null>(selectedOption);
    const [keyboardSelectedOption, setKeyboardSelectedOption] = useState<DropdownOption | null>(null);
    const [filteredOptions, setFilteredOptions] = useState<DropdownOption[]>(optionsList);
    const [noResults, setNoResults] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [iconLink, setIconLink] = useState<string | JSX.Element>('');
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [customValue, setCustomValue] = useState('');
    const [dontTriggerBlur, setDontTriggerBlur] = useState(false);
    const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');

    const calculateDropdownDirection = useCallback(() => {
        if (!dropdownRef.current) return;

        const rect = dropdownRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 300;

        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            setDropdownDirection('up');
        } else {
            setDropdownDirection('down');
        }
    }, []);

    useEffect(() => {
        setSelection(selectedOption);
        setSearchTerm(selection?.label ?? '');
    }, [selectedOption]);

    useUpdateEffect(() => {
        if (disabled || collapseDropdown) setShowList(false);
    }, [disabled, collapseDropdown]);

    useEffect(() => {
        if (disabled) {
            setIconLink('');
        } else if (selectedOption && selectedOption.id) {
            let tempIconLink: string | JSX.Element = '';
            optionsList.forEach((option) => {
                if (option.id === selectedOption.id) {
                    tempIconLink = option.icon ?? '';
                }
            });
            setIconLink(tempIconLink);
        }
    }, [disabled, selectedOption, optionsList]);

    useEffect(() => {
        setFilteredOptions(optionsList);
    }, [optionsList]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (showCustomInput && customValue.trim()) {
                    const customOption: DropdownOption = {
                        id: `custom_${Date.now()}`,
                        label: customValue.trim(),
                    };
                    itemClickHandler(customOption);
                    setShowCustomInput(false);
                    setCustomValue('');
                } else if (showCustomInput) {
                    setShowCustomInput(false);
                    setCustomValue('');
                }
                setShowList(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef, showCustomInput, customValue, itemClickHandler]);

    useEffect(() => {
        if (showCustomInput) {
            inputRef.current?.focus();
        }
    }, [showCustomInput]);

    const resetSearchTermAndFilteredOptions = useCallback(
        (value: string) => {
            setSearchTerm(value);
            changeFilterOptionsFromValue(value);
        },
        [optionsList, enableSearch],
    );

    const changeFilterOptionsFromValue = useCallback(
        (value: string) => {
            let filter = [...optionsList];
            if (enableSearch && value) {
                filter = optionsList.filter((option) => option.label.toLowerCase().includes(value.toLowerCase()));
                if (filter.length === 0) {
                    setNoResults(true);
                    if (hideOtherOptionWhenNoMatch) {
                        filter = [{ label: 'No match found', id: 'no-match', notSelectable: true }];
                    } else {
                        filter = [{ label: 'Other', id: 0 }];
                    }
                } else {
                    setNoResults(false);
                }
            }
            setFilteredOptions(filter);
        },
        [optionsList, enableSearch, hideOtherOptionWhenNoMatch],
    );

    useEffect(() => {
        // setIconLink as soon as the component mounts and search from optionsList
        if (!disabled) {
            let tempIconLink: string | JSX.Element = '';
            optionsList.forEach((option) => {
                if (option.label === selectedOption?.label) {
                    tempIconLink = option.icon ?? '';
                }
            });
            setIconLink(tempIconLink);
        }
    }, [optionsList, selectedOption, disabled]);

    const onSearchTermChange = useCallback(
        (value: string) => {
            if (showCustomInput) {
                setCustomValue(value);
                return;
            }

            setSearchTerm(value);
            changeFilterOptionsFromValue(value);
            setSelection(value !== selectedOption?.label ? null : selectedOption);
            if (!showList && enableSearch) {
                calculateDropdownDirection();
                setShowList(true);
            }
            setKeyboardSelectedOption(null);
            if (value) setNoResults(false);
        },
        [changeFilterOptionsFromValue, showList, selectedOption, showCustomInput, enableSearch, calculateDropdownDirection],
    );
    const onOptionClick = useCallback(
        (option: DropdownOption) => {
            if (option.notSelectable) {
                setSelection(null);
                setSearchTerm('');
                setFilteredOptions(optionsList);
                setShowList(false);
                setIconLink('');
                return;
            }
            setIconLink(option?.icon ?? '');
            itemClickHandler(option);
            setSearchTerm(option.label);
            setSelection(option);
            setNoResults(false);
            inputRef.current?.blur?.();
        },
        [itemClickHandler, optionsList],
    );

    const getDisplayValue = () => {
        if (showCustomInput) return customValue;
        if (document.activeElement === inputRef.current && enableSearch) return searchTerm;
        return selection?.label || '';
    };

    const closeListOnBlur = () => {
        setTimeout(() => {
            if (dontTriggerBlur) {
                setDontTriggerBlur(false);
                return;
            }

            if (showCustomInput) {
                if (customValue.trim()) {
                    const customOption: DropdownOption = {
                        id: `custom_${Date.now()}`,
                        label: customValue.trim(),
                    };
                    itemClickHandler(customOption);
                }
                setShowCustomInput(false);
                setCustomValue('');
                return;
            }

            setShowList(false);
            resetSearchTermAndFilteredOptions(selection ? selection.label : '');
        }, 0);
    };

    const handleRemove = () => {
        if (showCustomInput) {
            setShowCustomInput(false);
            setCustomValue('');
            return;
        }

        setSelection(null);
        setIconLink('');
        itemClickHandler({ id: '', label: '' });
        resetSearchTermAndFilteredOptions('');
    };

    const dropdownIcon = useMemo(() => {
        if (disabled) return null;

        if (showCustomInput) {
            return (
                <img
                    className={styles.DropdownIcon}
                    src={crossBtnGrey}
                    alt="cross"
                    width="16px"
                    height="16px"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRemove();
                    }}
                    style={{ cursor: 'pointer' }}
                />
            );
        }

        return (
            <img
                className={clsx(styles.DropdownIcon, {
                    [styles.DropdownIconInverted]: showList && !enableSearch,
                })}
                src={enableSearch && !showCustomInput ? searchIcon : bottomAngleIcon}
                alt="icon"
                width={enableSearch && !showCustomInput ? '16px' : '24px'}
                height={enableSearch && !showCustomInput ? '16px' : '24px'}
            />
        );
    }, [disabled, enableSearch, showList, showCustomInput, handleRemove]);

    const keyNavigation = useCallback(
        (e) => {
            const { key } = e;

            if (showCustomInput && key === 'Enter') {
                e.preventDefault();
                closeListOnBlur();
                return;
            }

            if (key === 'Tab') {
                closeListOnBlur();
                return;
            }

            if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter') {
                e.preventDefault();
            }

            if (!keyboardSelectedOption && key === 'ArrowDown') {
                setKeyboardSelectedOption(filteredOptions?.[0]);
            } else if (keyboardSelectedOption) {
                const currentIndex = filteredOptions.findIndex((option) => option.id === keyboardSelectedOption.id);
                let nextIndex = currentIndex;

                if (key === 'ArrowDown') {
                    nextIndex = currentIndex + 1;
                } else if (key === 'ArrowUp') {
                    nextIndex = currentIndex - 1;
                } else if (key === 'Enter') {
                    onOptionClick(keyboardSelectedOption);
                    return;
                }

                const nextOption = filteredOptions[nextIndex];
                if (nextOption) {
                    setKeyboardSelectedOption(nextOption);
                    document.getElementById(`option-${nextIndex}`)?.scrollIntoView({
                        block: 'nearest',
                        inline: 'nearest',
                        behavior: 'smooth',
                    });
                }
            }
        },
        [keyboardSelectedOption, filteredOptions, onOptionClick, showCustomInput, closeListOnBlur],
    );

    const shouldShowLabel =
        showFloatingLabel &&
        label &&
        ((selection?.id !== null && selection?.label !== '' && selection?.label !== undefined) || showCustomInput);

    return (
        <div
            ref={dropdownRef}
            className={clsx(styles.Dropdown, {
                [styles.Error]: (hasError || noResults) && !!errorMessage,
                [styles.Disabled]: disabled,
                [styles.CustomInputMode]: showCustomInput,
            })}
            onClick={() => {
                if (showCustomInput) {
                    inputRef.current?.focus?.();
                    return;
                }
                if (enableSearch && !showList) setSearchTerm(selection?.label || '');
                if (!showList) {
                    inputRef.current?.focus?.();
                    calculateDropdownDirection();
                }
                setShowList(!showList);
            }}
            onBlur={closeListOnBlur}
            onKeyDown={keyNavigation}
            onMouseDown={(e) => e.preventDefault()}
            style={style}
        >
            {shouldShowLabel && <label className={styles.Label}>{label}</label>}
            <div
                className={clsx(styles.Main, {
                    [styles.White]: variant === 'white',
                    [styles.Tiny]: size === 'tiny',
                    [styles.hasData]: showList || selection?.label || showCustomInput,
                    [styles.noFocus]: selection && !showList && !showCustomInput,
                    [styles.hasLabel]: customClass && (showList || selection?.label || showCustomInput),
                })}
            >
                <div className={styles.IconContainer}>
                    {iconLink ? (
                        typeof iconLink === 'string' ? (
                            <img src={iconLink} alt="icon" className={styles.IconStuff} />
                        ) : (
                            <span className={styles.IconStuff}>{iconLink}</span>
                        )
                    ) : null}
                    <input
                        id="input"
                        className={clsx(styles.Input, {
                            [styles.SelectedValue]: selection || document.activeElement === inputRef.current || showCustomInput,
                            [styles.Frozen]: !enableSearch && !showCustomInput,
                            [styles.White]: variant === 'white',
                            [styles.hasData1]: !!getDisplayValue() || showCustomInput,
                        })}
                        style={iconLink ? { ...inputStyle, paddingLeft: '1rem' } : inputStyle}
                        value={getDisplayValue()}
                        onChange={(e) => onSearchTermChange(e.target.value)}
                        placeholder={showCustomInput ? customOption?.placeholder : placeholder}
                        onFocus={() => {
                            if (!showCustomInput) {
                                setSearchTerm(enableSearch && selection?.label ? selection.label + ' ' : '');
                            }
                        }}
                        onClick={(e) => {
                            if (showCustomInput) {
                                e.stopPropagation();
                            }
                        }}
                        disabled={disabled}
                        readOnly={!enableSearch && !showCustomInput}
                        maxLength={showCustomInput ? customOption?.maxLength : undefined}
                        ref={inputRef}
                    />
                </div>
                {(searchTerm && !hideClearIcon) || showCustomInput ? (
                    <>
                        {disabled ? (
                            <></>
                        ) : showCustomInput ? (
                            dropdownIcon
                        ) : (
                            <img src={crossBtnGrey} style={{ paddingRight: '0rem' }} alt="removeIcon" onClick={handleRemove} />
                        )}
                    </>
                ) : (
                    dropdownIcon
                )}
                {showList && (
                    <div
                        style={{
                            ...listStyles,
                            ...(dropdownDirection === 'up' && {
                                top: 'auto',
                                bottom: 'calc(100% + 10px)',
                            }),
                        }}
                        className={clsx(styles.List, {
                            [styles.ListUp]: dropdownDirection === 'up',
                        })}
                    >
                        {filteredOptions.map((option, index) => (
                            <div
                                id={`option-${index}`}
                                onMouseDown={() => onOptionClick(option)}
                                key={option.id}
                                className={clsx(styles.ListItem, {
                                    [styles.ItemHovered]: keyboardSelectedOption?.id === option.id,
                                })}
                            >
                                {option.icon &&
                                    (typeof option.icon === 'string' ? (
                                        <img src={option.icon} alt="icon" className={styles.Icon} />
                                    ) : (
                                        <span className={styles.Icon}>{option.icon}</span>
                                    ))}
                                {option.label}
                            </div>
                        ))}
                        {customOption?.enabled && (
                            <div
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDontTriggerBlur(true);
                                    setShowList(false);
                                    setShowCustomInput(true);
                                    setCustomValue('');
                                    setSearchTerm('');
                                    setFilteredOptions(optionsList);
                                    setTimeout(() => {
                                        inputRef.current?.focus();
                                        setDontTriggerBlur(false);
                                    }, 100);
                                }}
                                className={clsx(styles.ListItem, styles.CustomOption)}
                                style={{
                                    color: 'var(--primary-text-colour)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    cursor: 'pointer',
                                }}
                            >
                                {customOption.icon ? (
                                    typeof customOption.icon === 'string' ? (
                                        <img src={customOption.icon} alt="icon" height={12} width={12} />
                                    ) : (
                                        customOption.icon
                                    )
                                ) : (
                                    <img src={plusPurpleCircle} alt="icon" height={12} width={12} />
                                )}
                                {customOption.text}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {!isEmpty(errorMessage) && (hasError || noResults) && (
                <div
                    className={clsx(styles.ErrorMsg, {
                        [styles.HideErrorMsg]: !(hasError || noResults),
                    })}
                >
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default Dropdown;
